import { useCallback, useEffect, useRef, useState } from "react";

import type * as types from "types";

/**
 * Distance (px) from the bottom within which the viewport is considered
 * "pinned". A small tolerance absorbs sub-pixel rounding and lets a near-bottom
 * scroll still count as following the stream.
 */
const PINNED_THRESHOLD_PX = 60;

interface UseStickToBottomArgs {
  containerRef: React.RefObject<HTMLDivElement | null>;
  messages: types.Message[];
  activeChatId?: string | null;
}

interface UseStickToBottomResult {
  isPinned: boolean;
  /** Jump to the live bottom of the scroll container and resume following. */
  scrollToBottom: () => void;
  /**
   * Callback ref to attach to the growing content wrapper. A callback ref (not
   * an object ref) lets the ResizeObserver re-attach if the content element
   * mounts after the hook runs (e.g. messages load asynchronously).
   */
  contentRef: (node: HTMLDivElement | null) => void;
}

/**
 * Implements "follow vs. pause" auto-scrolling for a streaming message list.
 *
 * The viewport auto-follows new content only while pinned to the bottom. When
 * the user scrolls away, following pauses; it resumes when they return to the
 * bottom (or send a new message / switch chats).
 */
export function useStickToBottom({
  containerRef,
  messages,
  activeChatId,
}: UseStickToBottomArgs): UseStickToBottomResult {
  const [isPinned, setIsPinned] = useState(true);
  // Mirror of isPinned for use inside imperative callbacks (scroll handler,
  // ResizeObserver). Kept in sync with the state setter below so callbacks read
  // the current value synchronously, without a render-cycle lag.
  const isPinnedRef = useRef(true);
  const setPinned = useCallback((pinned: boolean) => {
    isPinnedRef.current = pinned;
    setIsPinned(pinned);
  }, []);

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    // Jump instantly to the live bottom. A smooth animation captures a stale
    // scrollHeight and chases a target that keeps receding during fast
    // streaming, and its intermediate scroll events land short of the bottom,
    // momentarily re-tripping the pause heuristic (button flicker). An instant
    // assignment also fires a scroll event, but it lands at the bottom
    // (distance ≈ 0), so the heuristic stays pinned. Re-pin synchronously so a
    // content resize firing before the next render still follows.
    container.scrollTop = container.scrollHeight;
    setPinned(true);
  }, [containerRef, setPinned]);

  // Track user scroll intent: pinned when within threshold of the bottom.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      setPinned(distanceFromBottom <= PINNED_THRESHOLD_PX);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [containerRef, setPinned]);

  // Follow content growth (streaming chunks, async markdown/mermaid/vega
  // rendering) while pinned. Observe the content element, not the fixed-height
  // container, so late layout changes still trigger a follow. A callback ref
  // (re)attaches the observer whenever the content node mounts or unmounts,
  // which matters because the content wrapper appears only once messages load.
  const observerRef = useRef<ResizeObserver | null>(null);
  const contentRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      if (!node) return;
      const observer = new ResizeObserver(() => {
        if (isPinnedRef.current) {
          const container = containerRef.current;
          if (container) container.scrollTop = container.scrollHeight;
        }
      });
      observer.observe(node);
      observerRef.current = observer;
    },
    [containerRef]
  );

  // Re-pin and jump to the bottom when the user sends a new message, even if
  // they had scrolled up.
  const lastMessageUuid = messages.at(-1)?.uuid;
  const lastMessageRole = messages.at(-1)?.role;
  useEffect(() => {
    if (lastMessageRole === "user") {
      // Re-pinning is the intended reaction to a new send (scrollToBottom
      // sets pinned state).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      scrollToBottom();
    }
  }, [lastMessageUuid, lastMessageRole, scrollToBottom]);

  // Reset to the bottom when switching chats.
  useEffect(() => {
    // Re-pinning is the intended reaction to a chat switch.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    scrollToBottom();
  }, [activeChatId, scrollToBottom]);

  return { isPinned, scrollToBottom, contentRef };
}
